import React, { useContext, useEffect, useState} from 'react'
import {
  makeStyles,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  Button
} from '@material-ui/core'

import ResponsiveTable from '../../components/ResponsiveTable'
import EmptyState from '../../components/EmptyState'

import * as DateTransformer from '../../libraries/DateTransformer'
import TranslationLabel from '../../../../core/components/TranslationLabel'
import { CoreContext } from '../../../../core/context/CoreContext'
import { BasketContext } from '../../../../core/context/BasketContext'

import DatePicker from '../../../../shared/DatePicker'

const useStyles = makeStyles((theme) => ({
  campaignPageRoot: {
    width: 'calc(100% + 32px)',
    margin: '-8px 20px 0 -16px',
    flexGrow: 1,
    background: 'white',
    display: 'flex',
    flexFlow: 'column',
    height: 'calc(100% - 31px)',
    position: 'relative',

    [theme.breakpoints.up('sm')]: {
      margin: '-17px 20px 0 -16px',
    },
  },
  campaignPageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: '#EAEAEA',
    padding: '10px 15px',

    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column-reverse'
    },
  },
  campaignPageSubHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 15px',
    borderBottom: '1px solid rgba(0, 0, 0, .12)',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
    },
  },
  campaignPageHeaderResults: {
    display: 'flex',
    alignItems: 'center',
  },
  campaignPageSettings: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',

    [theme.breakpoints.down('md')]: {
      marginBottom: 10,
      width: '100%'
    },
  },
  datePicker: {
    position: 'absolute',
    right: 15,
    width: 200,
    backgroundColor: 'white',
    borderRadius: 3,
  },
}))

const headers = [
  'campaginId',
  'campaginName',
  'campaginFromDate',
  'campaginToDate',
  'deliveryFromDate',
  'deliveryToDate',
  ''
];

const dataKeys = [
  'campaignId',
  'campaignName',
  'campaignFromDate',
  'campaignToDate',
  'shippingFromDate',
  'shippingToDate',
  'campaignIdLink'
]

export default function CampaignPageOverView() {
  const classes = useStyles()

  const core = useContext(CoreContext)
  const basket = useContext(BasketContext)

  const [campaigns, setCampaigns] = useState([])
  const [activeCampaigns, setActiveCampaigns] = useState(true)
  const [futureCampaign, setFutureCampaign] = useState(false)
  const [_, setRequestedShippingDate] = useState(new Date())

  useEffect(() => {
    core.dataStore.GetCampaignsByCustomerNo(core.user.selectedCustomer.accountNo).then(res => {
      setCampaigns(
        res.data.filter(campagin => {
          if (activeCampaigns && !campagin.futureCampaign) {
            return campagin
          }

          if (futureCampaign && campagin.futureCampaign) {
            return campagin
          }
        })
      )
    })
  }, [core, futureCampaign, activeCampaigns])

  const setDate = x => {
    const date = new Date(x.setHours(12, 0, 0, 0))
    basket.basket.requestedShippingDate = `${date.getFullYear()}-${String((date.getMonth() + 1)).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00`
    setRequestedShippingDate(x)
  }

  const campaignIdLinkTextStyle = (campaign) => {
    if (isGoToButtonLighter(campaign))
      return { backgroundColor: "#3f51b5", color: "#fff" }
    else
      return { backgroundColor: "#757de8", color: "#fff" }
  };

  const getValueFromKey = (key, campaign) => {
    switch (key) {
      case 'campaignId':
        return campaign.campaignId
      case 'campaignName':
        return campaign.campaignName
      case 'campaignFromDate':
        return DateTransformer.formatDate(campaign.campaignFromDate)
      case 'campaignToDate':
        return DateTransformer.formatDate(campaign.campaignToDate)
      case 'shippingFromDate':
        return DateTransformer.formatDate(campaign.shippingFromDate)
      case 'shippingToDate':
        return DateTransformer.formatDate(campaign.shippingToDate)
      case 'campaignIdLink':
        return (<Button
          variant='contained'
          style={campaignIdLinkTextStyle(campaign)}
          onClick={() => core.history.push('/sales/campaign/' + campaign.campaignId)}>
          <TranslationLabel name='goToCampaign' />
        </Button>)
      default:
        return null
      }
  }

  const isGoToButtonLighter = (campaign) => {
    const today = new Date().setHours(12, 0, 0, 0)
    if (today <= new Date(campaign.campaignFromDate) || today >= new Date(campaign.campaignToDate))
    {
      return false;
    }

    const currentSelectedDate = currentDateValue().setHours(12, 0, 0, 0)
    return currentSelectedDate >= new Date(campaign.shippingFromDate) && currentSelectedDate <= new Date(campaign.shippingToDate)
  }

  const currentDateValue = () => {
    if (basket.basket.requestedShippingDate) {
      return new Date(basket.basket.requestedShippingDate)
    }

    const date = new Date()
    return new Date(date.setDate(date.getDate() + 1))
  }

  return (
    <div className={classes.campaignPageRoot}>
      <div className={classes.campaignPageHeader}>
        <h3><TranslationLabel name='campaigns' /></h3>

        <DatePicker
          autoOk
          size='small'
          disablePast
          variant='inline'
          inputVariant='outlined'
          label={<TranslationLabel name='chooseShippingDate' />}
          maxDate={new Date(Math.max(...campaigns.map(x => new Date(x.shippingToDate))))}
          onChange={e => setDate(e)}
          className={classes.datePicker}
          disabled={basket.basket.entries.find(x => x.campaignId != null)}
          value={currentDateValue()}
        />
      </div>

      <div className={classes.campaignPageSubHeader}>
        <div className={classes.campaignPageHeaderResults}>
          <div>
            <Typography>
              <TranslationLabel name='displaying' /> {campaigns.length} <TranslationLabel name='results' />
            </Typography>
          </div>
        </div>

        <div className={classes.campaignPageSettings}>
          <FormControl>
            <FormControlLabel
              label={<TranslationLabel name='activeCampains' />}
              control={
                <Switch
                  checked={activeCampaigns}
                  onChange={() => {
                    setActiveCampaigns(!activeCampaigns)
                    setFutureCampaign(false)
                  }}
                  color='primary'
                />
              }
            />
          </FormControl>

          <FormControl>
            <FormControlLabel
              label={<TranslationLabel name='futureCampaigns' />}
              control={
                <Switch
                  checked={futureCampaign}
                  onChange={() => {
                    setFutureCampaign(!futureCampaign)
                    setActiveCampaigns(false)
                  }}
                  color='primary'
                />
              }
            />
          </FormControl>
        </div>
      </div>

      {campaigns.length !== 0 && (
        <div style={{marginTop: '25px'}}>
          <ResponsiveTable
            headers={headers}
            rows={campaigns.map((campagin) => dataKeys.map((key) => getValueFromKey(key, campagin)))}
          />
        </div>
      )}

      {campaigns.length === 0 && (
          <EmptyState
            main='CampaignOverviewEmptyMain'
            sub='CampaignOverviewEmptyMain'
          />
      )}
    </div>
  )
}
